import { useRouter } from 'next/router';

import { IS_LOCAL } from '@/constants/env';
import { DynamicLinkOptions, EAirbridgeLinkType } from '@/shared/types';
import Airbridge, { TrackingLinkOptions } from '@/utils/airbridge';
import Firebase from '@/utils/firebase';
import { getPageName } from '@/utils/logger';

const AIRBRIDGE_PARAM_PREFIX = 'ab_';

const sanitizeEventName = (eventName: string): string => {
  return eventName.replace(/[^\w]/g, '_');
};

const formatOptionsForLogging = (options: DynamicLinkOptions) => {
  return Object.fromEntries(
    Object.entries(options).map(([key, value]) => [
      `${AIRBRIDGE_PARAM_PREFIX}${key}`,
      typeof value === 'number' ? value.toString() : value,
    ])
  );
};

interface UseAirbridgeDynamicReturn {
  navigateWithTracking: (eventName: string, options: DynamicLinkOptions, linkType?: EAirbridgeLinkType) => void;
  getDynamicLink: (options: DynamicLinkOptions, linkType?: EAirbridgeLinkType) => string;
  getTrackingParams: (linkType?: EAirbridgeLinkType) => string;
  createTrackingLinkAsync: (channel: string, options: TrackingLinkOptions) => Promise<string>;
  logToFirebase: (eventName: string, options: DynamicLinkOptions) => void;
}

const useAirbridgeDynamicLink = (
  channel = 'web_organic',
  initOptions?: DynamicLinkOptions
): UseAirbridgeDynamicReturn => {
  const router = useRouter();

  const getDynamicLink = (options: DynamicLinkOptions, linkType: EAirbridgeLinkType = EAirbridgeLinkType.GENERAL) => {
    return Airbridge.getDynamicLink({ channel, ...initOptions, ...options }, linkType);
  };

  const getTrackingParams = (linkType: EAirbridgeLinkType = EAirbridgeLinkType.GENERAL) => {
    return Airbridge.getTrackingParams(linkType);
  };

  const createTrackingLinkAsync = async (channel: string, options: TrackingLinkOptions): Promise<string> => {
    return Airbridge.createTrackingLinkAsync(channel, options);
  };

  const logToFirebase = (eventName: string, options: DynamicLinkOptions) => {
    const pageName = getPageName(router);
    const sanitizedEventName = sanitizeEventName(eventName);
    const formattedOptions = formatOptionsForLogging({ ...initOptions, ...options });
    const payload = { page_name: pageName, ...formattedOptions };

    if (IS_LOCAL) {
      console.log('logger >>', { eventName: sanitizedEventName, ...payload });
      return;
    }

    Firebase.logEventOnlyFirebase(sanitizedEventName, payload);
  };

  const navigateWithTracking = (
    eventName: string,
    options: DynamicLinkOptions,
    linkType: EAirbridgeLinkType = EAirbridgeLinkType.GENERAL
  ) => {
    const customOptions = { ...initOptions, ...options };
    logToFirebase(eventName, customOptions);

    Airbridge.setDynamicLink(
      {
        channel,
        ...customOptions,
      },
      linkType
    );
  };

  return {
    navigateWithTracking,
    getDynamicLink,
    getTrackingParams,
    logToFirebase,
    createTrackingLinkAsync,
  };
};

export default useAirbridgeDynamicLink;
